import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { NotificationsTab } from "@/components/settings/NotificationsTab";
import { PasswordTab } from "@/components/settings/PasswordTab";
import { User, Bell, Lock, Camera } from "lucide-react";
import { useTabParam, type TabDef } from "@/hooks/useTabParam";
import { LinkTabs } from "@/components/ui/link-tabs";

/**
 * AUT-399: these three were Radix `TabsTrigger`s with no URL behind them, so none of them could be
 * opened in a new tab -- the whole point of the ticket. They now carry `?tab=<id>` and render as
 * real anchors, the same treatment `BrandIntelligence` already had from AUT-256.
 */
const PROFILE_TABS: TabDef[] = [
  { id: "profile", label: "Profile", icon: User },
  { id: "notifications", label: "Notifications", icon: Bell },
  { id: "security", label: "Security", icon: Lock },
];

const DEFAULT_PROFILE_TAB = "profile";
import { useGetAccount, useUpdateProfile } from "@/resources/account/account.api";
import { handleApiError } from "@/utils";
import { ACCOUNT_ROLE, ACCOUNT_ROLE_LABELS, ACCOUNT_ROLE_BADGES } from "@/constants";
import { UpdateProfileParams } from "@/types";

function ProfileTab() {
  const { data: account } = useGetAccount();
  const { mutate: updateProfile, isPending } = useUpdateProfile();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [jobTitle, setJobTitle] = useState("");

  useEffect(() => {
    if (account) {
      setFirstName(account.first_name ?? "");
      setLastName(account.last_name ?? "");
      setEmail(account.email);
      setCompanyName(account.organization?.name ?? "");
      setJobTitle(account.job_title ?? "");
    }
  }, [account]);

  const isAdmin =
    account?.role === ACCOUNT_ROLE.OWNER ||
    account?.role === ACCOUNT_ROLE.ADMIN;

  const initials = ((firstName[0] ?? "") + (lastName[0] ?? "")).toUpperCase();

  const roleLabel = account?.role ? ACCOUNT_ROLE_LABELS[account.role] : "";
  const roleBadgeVariant = account?.role ? ACCOUNT_ROLE_BADGES[account.role] : "secondary";

  const handleSave = () => {
    const payload: UpdateProfileParams = {
      first_name: firstName,
      last_name: lastName,
      job_title: jobTitle,
      ...(isAdmin ? { company_name: companyName } : {}),
    };
    updateProfile(payload, {
      onSuccess: () => toast.success("Profile updated"),
      onError: (error) => handleApiError(error),
    });
  };

  return (
    <div className="max-w-2xl space-y-6">
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-6">
            <div className="relative group">
              <Avatar className="h-20 w-20 bg-primary-tint-soft">
                <AvatarFallback className="bg-primary-tint-soft text-primary text-2xl font-semibold">
                  {initials ? initials : <User className="h-8 w-8" />}
                </AvatarFallback>
              </Avatar>
              <button className="absolute inset-0 flex items-center justify-center bg-foreground/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                <Camera className="h-5 w-5 text-background" />
              </button>
            </div>
            <div>
              <p className="text-lg font-semibold text-foreground">{firstName} {lastName}</p>
              <p className="text-sm text-muted-foreground">{email}</p>
              {account?.role && (
                <Badge variant={roleBadgeVariant} className="mt-1">
                  {roleLabel}
                </Badge>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Personal Information</CardTitle>
          <CardDescription>Update your name and contact details</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" value={email} disabled className="bg-muted" />
            <p className="text-xs text-muted-foreground">Contact support to change your email address</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="companyName">Company</Label>
              <Input
                id="companyName"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                disabled={!isAdmin}
                className={!isAdmin ? "bg-muted" : undefined}
              />
              {!isAdmin && (
                <p className="text-xs text-muted-foreground">Only admins can edit company name.</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="jobTitle">Job Title</Label>
              <Input
                id="jobTitle"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={isPending}>
          {isPending ? "Saving..." : "Save Changes"}
        </Button>
      </div>
    </div>
  );
}

export default function Profile() {
  const { activeTab, tabHref } = useTabParam(PROFILE_TABS, DEFAULT_PROFILE_TAB);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-semibold text-foreground">Profile Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">Manage your personal preferences and notifications</p>
      </div>

      <Tabs value={activeTab} className="w-full">
        <LinkTabs tabs={PROFILE_TABS} activeTab={activeTab} tabHref={tabHref} />

        <TabsContent value="profile" className="mt-6">
          <ProfileTab />
        </TabsContent>
        <TabsContent value="notifications" className="mt-6">
          <NotificationsTab />
        </TabsContent>
        <TabsContent value="security" className="mt-6">
          <PasswordTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
